import { z } from "zod";

/**
 * Betriebsstatus und Betriebslog (MCP-Spec §6). Status sieht jeder Angemeldete,
 * die Logzeilen nur `admin`.
 */

export const betriebLaufSchema = z.object({
  start: z.string(),
  ende: z.string().nullable(),
  status: z.string(),
  stats: z.record(z.string(), z.unknown()).nullable(),
  fehler: z.string().nullable(),
});
export type BetriebLauf = z.infer<typeof betriebLaufSchema>;

export const BETRIEB_WARNUNG_CODES = [
  "ERFOLG_ZU_ALT",
  "LETZTER_LAUF_GESCHEITERT",
  "LAEUFT_ZU_LANGE",
  "ACCOUNTS_VERSCHWUNDEN",
  "ACCOUNT_PRUEFUNG",
  "FEHLER_IM_LOG",
  "MAIL_FEHLGESCHLAGEN",
  "MAIL_STAU",
] as const;

export const betriebWarnungSchema = z.object({
  code: z.enum(BETRIEB_WARNUNG_CODES),
  text: z.string(),
  quelle: z.string().optional(),
});
export type BetriebWarnung = z.infer<typeof betriebWarnungSchema>;

export const betriebQuelleSchema = z.object({
  quelle: z.string(),
  letzterLauf: betriebLaufSchema.nullable(),
  letzterErfolg: betriebLaufSchema.nullable(),
});
export type BetriebQuelle = z.infer<typeof betriebQuelleSchema>;

export const betriebStatusSchema = z.object({
  version: z.string(),
  umgebung: z.string(),
  datenbank: z.enum(["ok", "fehler"]),
  quellen: z.array(betriebQuelleSchema),
  warnungen: z.array(betriebWarnungSchema),
});
export type BetriebStatus = z.infer<typeof betriebStatusSchema>;

export const BETRIEB_STUFEN = ["debug", "info", "warn", "error", "fatal"] as const;
export const betriebStufeSchema = z.enum(BETRIEB_STUFEN);
export type BetriebStufe = z.infer<typeof betriebStufeSchema>;

/** pino-Level je Stufenname; ein Filter auf eine Stufe meint „mindestens“. */
export const BETRIEB_STUFE_LEVEL: Record<BetriebStufe, number> = {
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  fatal: 60,
};

export const betriebLogsQuerySchema = z.object({
  seit: z.string().datetime({ offset: true }).optional(),
  bis: z.string().datetime({ offset: true }).optional(),
  stufe: betriebStufeSchema.optional(),
  dienst: z.string().min(1).max(40).optional(),
  job: z.string().min(1).max(120).optional(),
  suche: z.string().min(1).max(200).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(200),
});
export type BetriebLogsQuery = z.infer<typeof betriebLogsQuerySchema>;

export const betriebLogEintragSchema = z.object({
  zeit: z.string(),
  dienst: z.string(),
  stufe: z.number().int(),
  job: z.string().nullable(),
  nachricht: z.string(),
  daten: z.unknown().nullable(),
});
export type BetriebLogEintrag = z.infer<typeof betriebLogEintragSchema>;

export const betriebLogsSchema = z.object({
  eintraege: z.array(betriebLogEintragSchema),
});
export type BetriebLogs = z.infer<typeof betriebLogsSchema>;
